import { useMutation } from '@apollo/client';
import { FormikProvider, useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useUrlQuery } from '../../hooks/use-url-query';
import {
  CREATE_ADDITIONAL_PROFILE,
  CREATE_DEFAULT_PROFILE,
} from '../../http/apollo-queries';
import { Button, Icon, PopperComponent } from '../../components/UI';
import { Input } from '../../components/form';
import { usernameValidator } from './username.validator';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/Auth';
import { Modal } from '../../components/Modal';
import { trackEvent } from '../../helpers/analytics';

const ChooseUsername = () => {
  const { t } = useTranslation();
  const { refetch: refetchUser } = useAuth();

  const [createDefaultProfile, { loading: createDefaultProfileLoading }] =
    useMutation(CREATE_DEFAULT_PROFILE, {
      onCompleted: (data) => {
        refetchUser().then((res) => {
          navigate(`/upload-image`);
        });
      },
      onError: (err) => setUsernameApiError(err.message),
    });

  const [createAdditionalProfile, { loading: createAdditionalProfileLoading }] =
    useMutation(CREATE_ADDITIONAL_PROFILE, {
      onCompleted: (data) => {
        refetchUser().then((res) => {
          navigate(
            `/upload-image?profileId=${data.createAdditionalProfile.id}`
          );
        });
      },
      onError: (err) => setUsernameApiError(err.message),
    });

  const [usernameHintShown, setUsernameHintShown] = useState<boolean>(false);
  const [usernameApiError, setUsernameApiError] = useState<string | undefined>(
    undefined
  );
  const navigate = useNavigate();
  const query = useUrlQuery();

  const signupFlow = !query.get('newAccount');

  useEffect(() => {
    if (signupFlow) {
      trackEvent('signup_complete');
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      username: '',
    },
    onSubmit: () => {
      if (!signupFlow) {
        createAdditionalProfile({
          variables: { username: formik.values.username },
        }).then((res: any) =>
          refetchUser().then(() => {
            if (res?.data && !res?.errors) {
              navigate(
                `/upload-image?profileId=${res.data.createAdditionalProfile.id}`
              );
            }
          })
        );
      } else {
        createDefaultProfile({
          variables: { username: formik.values.username },
        }).then((res: any) =>
          refetchUser().then(() => {
            if (res?.data && !res?.errors) {
              navigate('/upload-image');
            }
          })
        );
      }
    },
    validateOnMount: true,
  });

  return (
    <Modal
      isOpen={true}
      backdrop={'solid'}
      handleMerlinLogo={true}
      isClosable={false}
      padding={'huge'}
    >
      <div className={'text-center'}>
        <h1 className={'text-black text-xl font-semibold mb-4 dark:text-white'}>
          {t(
            signupFlow
              ? 'chooseUsername.titleMainProfile'
              : 'chooseUsername.titleAdditionalProfile'
          )}
        </h1>
        <p className={'text-grey-50 text-sm whitespace-pre-wrap'}>
          {t(
            signupFlow
              ? 'chooseUsername.descriptionMainProfile'
              : 'chooseUsername.descriptionAdditionalProfile'
          )}
        </p>
        <div className={'flex flex-col items-center mt-8'}>
          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
              <PopperComponent
                options={{
                  placement: 'left',
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 10],
                      },
                    },
                    {
                      name: 'flip',
                      options: {
                        fallbackPlacements: ['top-start', 'bottom-start'],
                      },
                    },
                  ],
                }}
                contentClassName={
                  'w-72 text-left bg-white dark:bg-grey-90 dark:border-grey-70 border-grey-10 p-4 border rounded-xl shadow-md'
                }
                reference={
                  <>
                    <Input
                      className={'mx-auto'}
                      name="username"
                      formik={formik}
                      autoFocus
                      onBlur={() => setUsernameHintShown(false)}
                      onChange={() => setUsernameHintShown(true)}
                      id="username"
                      label={t('chooseUsername.form.label.username')}
                      size="medium"
                      type="text"
                      placeholder={t(
                        'chooseUsername.form.placeholder.username'
                      )}
                      validate={usernameValidator}
                      prefix={'@'}
                    />
                    {usernameApiError && (
                      <p className="text-negative text-sm">
                        {t('chooseUsername.error.' + usernameApiError)}
                      </p>
                    )}
                  </>
                }
                visible={usernameHintShown}
              >
                <div>
                  <h5 className="text-black text-sm font-semibold uppercase">
                    {t('chooseUsername.form.tooltip.title')}
                  </h5>
                  <p className="text-grey-50 text-sm mt-2">
                    {t('chooseUsername.form.tooltip.description')}
                  </p>
                  <div className="mt-5">
                    <div className="inline-flex items-center">
                      <span className={'flex-grow-1'}>
                        <Icon
                          svgName={'check'}
                          className={`
                          ${
                            formik.errors.username ? 'bg-grey-30' : 'bg-primary'
                          }
                          rounded-full
                          w-5 h-5 p-1
                          `}
                        />
                      </span>
                      <span className="text-sm text-black-2 pl-2">
                        {t('chooseUsername.form.tooltip.validator')}
                      </span>
                    </div>
                  </div>
                </div>
              </PopperComponent>
              <div className="mt-10 items-center">
                <Button
                  type="submit"
                  variant="primary"
                  buttonClass="w-64"
                  loading={
                    createDefaultProfileLoading ||
                    createAdditionalProfileLoading
                  }
                >
                  {t('chooseUsername.form.continue')}
                </Button>
              </div>
            </form>
          </FormikProvider>
        </div>
      </div>
    </Modal>
  );
};

export default ChooseUsername;
